import { z } from 'zod';

/**
 * An image URL: either absolute (provider CDN, e.g. Sabre) or a server media-proxy path
 * (`/api/media/...`) for providers whose direct URLs expire or embed credentials (Google).
 * Clients resolve proxy paths against their API base via `resolveMediaUrl`.
 */
const ImageUrlSchema = z.union([z.string().url(), z.string().regex(/^\/api\/media\//)]);

/**
 * Widget image schema - used for images in widgets and room images
 */
export const WidgetImageSchema = z.object({
  id: z.string(),
  url: ImageUrlSchema,
  caption: z.string().optional(),
  attribution: z.string().optional(),
  source: z.string().optional(),
  alt: z.string().optional(),
  roomTypeCodes: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  priority: z.number().optional(),
  /** Provider-native reference the media proxy resolves at request time (e.g. a Google photo resource name). */
  providerRef: z.string().optional(),
});

export type WidgetImage = z.infer<typeof WidgetImageSchema>;

/**
 * A single size rendition of a gallery image, as delivered by the media provider.
 */
export const ImageVariantSchema = z.object({
  url: z.string().url(),
  width: z.number().optional(),
  height: z.number().optional(),
});

export type ImageVariant = z.infer<typeof ImageVariantSchema>;

/**
 * A stored hotel-gallery image. Superset of `WidgetImageSchema` (every gallery image is a
 * valid widget image) plus provider metadata the cards don't need but the full-gallery
 * viewer and future providers do.
 *
 * `source` is an open string (e.g. "sabre", "travelport", "google-places") — never an enum,
 * so adding a provider is a data change, not a schema migration.
 */
export const GalleryImageSchema = z.object({
  id: z.string(),
  /** Canonical display URL — the largest rendition the provider offers, or a media-proxy path. */
  url: ImageUrlSchema,
  caption: z.string().optional(),
  attribution: z.string().optional(),
  source: z.string().optional(),
  alt: z.string().optional(),
  /** Provider room-type codes this image depicts (e.g. Sabre/Travelport room codes). */
  roomTypeCodes: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  priority: z.number().optional(),
  /** Provider-native reference the media proxy resolves at request time (e.g. a Google photo resource name). */
  providerRef: z.string().optional(),
  /** OTA Picture Category code (OpenTravel PIC, e.g. 1=Exterior, 6=Pool, 13=Guest room). */
  category: z.number().optional(),
  /** Size renditions keyed by tier. Providers fill what they have; `url` is the fallback. */
  variants: z
    .object({
      thumbnail: ImageVariantSchema.optional(),
      small: ImageVariantSchema.optional(),
      medium: ImageVariantSchema.optional(),
      large: ImageVariantSchema.optional(),
      original: ImageVariantSchema.optional(),
    })
    .optional(),
});

export type GalleryImage = z.infer<typeof GalleryImageSchema>;
